import { useEffect, useState } from 'react';
import { offlineSignBlock } from '../utils/offlineSigning';
import { subscribeSyncStatus } from '../utils/offlineSyncCoordinator';

/**
 * SW2-2459 (PR 2) — why a bulk sign must wait for offline work, or null. The bulk endpoints sign
 * many inspections in one request and have no queue, so an inspection of a downloaded project with
 * work still on the device can't be signed there: the report would go out without that work.
 * Re-checked whenever a sync finishes, so the block lifts by itself.
 */
export function useOfflineSignBlock(inspectionIds: number[]): string | null {
  // Blocked until the first check answers: the modal opens before IndexedDB does.
  const [block, setBlock] = useState<string | null>('Checking for offline work…');
  const key = inspectionIds.join(',');

  useEffect(() => {
    let live = true;
    // Only the latest check answers: an older one finishing last restored a stale block (PR #156).
    let latest = 0;
    const check = () => {
      const run = ++latest;

      offlineSignBlock(inspectionIds)
        .catch(() => 'Offline work could not be checked on this device. Try again.')
        .then((reason: string | null) => {
          if (live && run === latest) setBlock(reason);
        });
    };

    check();
    const unsubscribe = subscribeSyncStatus((status: any) => {
      if (!status.syncing) check();
    });

    return () => {
      live = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return block;
}
